import { NavioContactChatWidget } from '@components/BookingFlow/Common/NavioContactChatWidget';
import { CarImage } from '@components/Common/CarImage';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { RedirectToStatusOrShowOfferStepWrapper } from '@components/TireOffer/RedirectToStatusOrShowOfferStepWrapper';
import { TireOfferExplanation } from '@components/TireOffer/TireOfferExplanation';
import { FindingsStatusBand, FindingsStatusCard } from '@components/TireOffer/TireOfferLanding/FindingsStatusCard';
import { formatMm } from '@components/TireOffer/TireOfferLanding/formatMm';
import {
  getPrimaryTireLeadReason,
  getTireLeadReasonCopy
} from '@components/TireOffer/TireOfferLanding/tireLeadReasonCopy';
import { TireOfferDeclineSheet } from '@components/TireOffer/TireOfferLanding/TireOfferDeclineSheet';
import { TireOfferRemindSheet } from '@components/TireOffer/TireOfferLanding/TireOfferRemindSheet';
import { TireOfferWhySheet } from '@components/TireOffer/TireOfferLanding/TireOfferWhySheet';
import { WornTreadStatusBand, WornTreadStatusCard } from '@components/TireOffer/TireOfferLanding/WornTreadStatusCard';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { TireLeadReason, type TireQuoteRecordCustomer, TireTreadDepthStatus, type TreadWearRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { FixedBottomBarContainer, NoddiButton, NoddiCard, NoddiInfoBanner } from 'noddi-ui-web';
import {
  CustomDateFormats,
  customerRoutes,
  formatCarLicensePlate,
  noddiFormatDate,
  TIRE_SET_OFFER_ADMIN_VIEW_PARAM,
  type TreadDepthThresholds
} from 'noddi-util';
import { commonTrackingEvents, NavioTireSeasonChip, tracking } from 'noddi-web';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import { TireOfferDescription } from './TireOfferDescription';

type OpenSheet = 'why' | 'remind' | 'decline' | null;

export const TireOffer = () => {
  const { slug } = useParams() as { slug: string };
  const [searchParams] = useSearchParams();
  const { currentBrand } = useCurrentBrand();

  useEffect(() => {
    tracking.track(commonTrackingEvents.tireOfferViewed, { slug, brand: currentBrand });
  }, [slug, currentBrand]);

  noddiAsync.useGet({
    type: URLKeys.getTireSetOfferPageView,
    input: { carTireQuoteSlug: slug },
    queryConfig: { enabled: searchParams.get(TIRE_SET_OFFER_ADMIN_VIEW_PARAM) !== 'true', staleTime: Infinity }
  });

  return (
    <RedirectToStatusOrShowOfferStepWrapper>
      {(tireOffer) => <TireOfferLanding offer={tireOffer} slug={slug} />}
    </RedirectToStatusOrShowOfferStepWrapper>
  );
};

const TireOfferLanding = ({ offer, slug }: { offer: TireQuoteRecordCustomer; slug: string }) => {
  const navigate = useNavigate();
  const [openSheet, setOpenSheet] = useState<OpenSheet>(null);

  const lead = offer.lead ?? null;
  const treadWear = offer.treadWear ?? null;
  // An offer without a lead still explains worn tires from the car's measurements.
  const reason =
    lead !== null
      ? getPrimaryTireLeadReason(lead)
      : treadWear !== null && offer.treadDepthStatus !== TireTreadDepthStatus.ABOVE_RECOMMENDED
        ? TireLeadReason.WORN_TREAD
        : null;
  const reasonTireCount = lead
    ? lead.tireDetails.positions.filter((position) => reason !== null && position.reasons.includes(reason)).length
    : 0;
  const thresholds = getThresholds(offer);
  const season = offer.season.value;
  const latestMm = treadWear?.readings.at(-1)?.valueMm;
  const belowLegalLimit = thresholds !== null && latestMm != null && latestMm < thresholds.limitMm;
  const copy =
    reason !== null
      ? getTireLeadReasonCopy(reason, reasonTireCount, {
          season,
          limitMm: thresholds?.limitMm ?? null,
          belowLegalLimit
        })
      : null;
  const isWornTread = reason === TireLeadReason.WORN_TREAD && treadWear !== null;
  const carName = offer.carDisplayName;
  const licensePlate = formatCarLicensePlate(offer.car);
  const brandName = offer.serviceDepartment.brand.name;

  const openWhy = () => {
    tracking.track(commonTrackingEvents.tireOfferExplanationOpened, { slug, sheet: 'why' });
    setOpenSheet('why');
  };
  const goToSelection = () => void navigate(customerRoutes.tireOfferSelection.getPath({ slug }));
  const closeSheet = (open: boolean) => !open && setOpenSheet(null);
  const remindOn = offer.remindAt
    ? noddiFormatDate(offer.remindAt, CustomDateFormats.SHORT_WEEKDAY_DAY_MONTH_COMMA)
    : '';
  const canRespond = !offer.isRejected && (offer.reminderOptions ?? []).length > 0;
  const openRemind = canRespond ? () => setOpenSheet('remind') : undefined;
  const hasWhy = copy !== null && reason !== TireLeadReason.CUSTOMER_WISH;
  const heroSummary = isWornTread ? getWornTreadSummary(treadWear, thresholds, belowLegalLimit) : copy?.whyIntro;

  return (
    // TireOfferRouter sets the worker app's screen color for the flow: the car photo's studio grey blends into it.
    <ContentWrapperCustomerApp width='xl' hideBackButton>
      {/* Phones and tablets stack the sections; from lg the page follows the design's desktop bands. */}
      <div className='flex flex-col gap-10 pb-24 md:pb-0 lg:gap-14'>
        {/* The worker app's car header: the text holds the left, the car runs past the screen edge on phones.
            The negative right margin cancels the page gutter, so the screen edge crops the car. The photo has empty
            space around the car, so on phones the hero is shorter than the photo and sits closer to the card. */}
        <div className='relative -mr-5 -mb-5 flex min-h-36 items-center overflow-x-clip sm:-mr-10 md:mr-0 md:mb-0 md:min-h-64'>
          <CarImage car={{ image: offer.carImage, make: offer.car.make }} alt={carName} size='header' />
          <div className='relative flex max-w-[56%] flex-col gap-2 md:max-w-1/2 lg:gap-4'>
            <span className='text-sm font-medium text-muted-foreground md:text-base'>
              {licensePlate ? `${carName} · ${licensePlate}` : carName}
            </span>
            <h1 className='text-3xl font-bold text-balance text-primary md:text-5xl'>
              {/* One headline for every offer; the status card below says what was found. */}
              <Trans>Your tires should be replaced</Trans>
            </h1>
            <NavioTireSeasonChip
              season={season}
              className='self-start'
              translations={{
                summerTires: t`For your summer tires`,
                winterTires: t`For your winter tires`,
                allSeasonTires: t`For your all-season tires`
              }}
            />
            {heroSummary && <p className='hidden text-lg text-foreground/80 lg:block'>{heroSummary}</p>}
            <div className='hidden gap-3 pt-2 lg:flex'>
              <NoddiButton onClick={goToSelection}>
                <Trans>See tire options</Trans>
              </NoddiButton>
              {openRemind && (
                <NoddiButton variant='outline' startIcon='Bell' onClick={openRemind}>
                  <Trans>Remind me later</Trans>
                </NoddiButton>
              )}
            </div>
          </div>
        </div>

        {copy === null && (
          <div className='flex flex-col gap-4'>
            <p className='text-muted-foreground'>
              <TireOfferDescription car={offer.car} treadDepthStatus={offer.treadDepthStatus as TireTreadDepthStatus} />
            </p>
            {/* Without a status card to hold it, phones get the reminder here; desktop has it in the hero. */}
            {openRemind && (
              <NoddiButton variant='outline' startIcon='Bell' className='w-full lg:hidden' onClick={openRemind}>
                <Trans>Remind me later</Trans>
              </NoddiButton>
            )}
          </div>
        )}
        {isWornTread && copy && (
          <>
            <div className='lg:hidden'>
              <WornTreadStatusCard
                treadWear={treadWear}
                thresholds={thresholds}
                copy={copy}
                season={season}
                belowLegalLimit={belowLegalLimit}
                onOpenWhy={openWhy}
                onOpenRemind={openRemind}
              />
            </div>
            <div className='hidden lg:block'>
              <WornTreadStatusBand
                treadWear={treadWear}
                thresholds={thresholds}
                copy={copy}
                season={season}
                belowLegalLimit={belowLegalLimit}
                onOpenWhy={openWhy}
              />
            </div>
          </>
        )}
        {!isWornTread && lead && reason && copy && (
          <>
            <div className='lg:hidden'>
              <FindingsStatusCard
                lead={lead}
                reason={reason}
                copy={copy}
                onOpenWhy={openWhy}
                onOpenRemind={openRemind}
              />
            </div>
            <div className='hidden lg:block'>
              <FindingsStatusBand lead={lead} reason={reason} copy={copy} onOpenWhy={openWhy} />
            </div>
          </>
        )}
        {offer.noteToCustomer && (
          <NoddiCard size='small' icon='MessageSquare' title={t`Information from ${brandName}`}>
            <div className='text-sm'>{offer.noteToCustomer}</div>
          </NoddiCard>
        )}

        {offer.isRejected && (
          <NoddiInfoBanner
            title={t`You declined this offer`}
            texts={[{ value: t`You can still choose tires here if you change your mind.` }]}
          />
        )}
        {offer.remindAt && !offer.isRejected && (
          <NoddiInfoBanner
            icon='Bell'
            title={t`Reminder set`}
            texts={[
              {
                value: t`We send you a text message on ${remindOn}.`
              }
            ]}
          />
        )}

        {/* The tire options are one tap away in the bottom bar, and in the hero on desktop. */}
        <div className='flex flex-col gap-4'>
          {canRespond && (
            <NoddiButton variant='link' className='self-center lg:order-last' onClick={() => setOpenSheet('decline')}>
              <Trans>Not interested?</Trans>
            </NoddiButton>
          )}
          <TireOfferExplanation hasWheelSetInStorageFacility={offer.hasWheelSetInStorageFacility} />
        </div>
      </div>

      {/* Every step of the flow ends in this bar, so chat and the next step stay in the same place. */}
      <FixedBottomBarContainer contentWidth='xl'>
        <div className='flex w-full items-center gap-2'>
          {/* Right of the action on phones, beside Back from md. */}
          <NavioContactChatWidget size='small' className='max-md:order-last' />
          <NoddiButton className='flex-1 md:ml-auto md:flex-none' onClick={goToSelection}>
            <Trans>See tire options</Trans>
          </NoddiButton>
        </div>
      </FixedBottomBarContainer>

      {copy && hasWhy && (
        <TireOfferWhySheet
          open={openSheet === 'why'}
          onOpenChange={closeSheet}
          copy={copy}
          lead={lead}
          treadWear={treadWear}
          thresholds={thresholds}
          season={season}
          isWornTread={isWornTread}
          onSeeOptions={goToSelection}
        />
      )}
      <TireOfferRemindSheet open={openSheet === 'remind'} onOpenChange={closeSheet} offer={offer} />
      <TireOfferDeclineSheet
        open={openSheet === 'decline'}
        onOpenChange={closeSheet}
        offer={offer}
        onRemindInstead={() => setOpenSheet('remind')}
      />
    </ContentWrapperCustomerApp>
  );
};

const getThresholds = (offer: TireQuoteRecordCustomer): TreadDepthThresholds | null => {
  const limitMm = offer.treadWear?.limitMm ?? offer.lead?.tireDetails.treadDepthLimitMm;
  const recommendedMm = offer.treadWear?.recommendedMm ?? offer.lead?.tireDetails.treadDepthRecommendedMm;
  return limitMm != null && recommendedMm != null ? { limitMm, recommendedMm } : null;
};

/** The desktop hero's line under the headline: what is left on the most worn tire, against the limit it is under. */
const getWornTreadSummary = (
  treadWear: TreadWearRecord,
  thresholds: TreadDepthThresholds | null,
  belowLegalLimit: boolean
) => {
  const latestMm = treadWear.readings.at(-1)?.valueMm ?? 0;
  const leftMm = formatMm(latestMm);
  const left = t`The most worn tire has ${leftMm} mm left.`;
  if (!thresholds || latestMm >= thresholds.recommendedMm) {
    return left;
  }
  if (belowLegalLimit) {
    const limitMm = formatMm(thresholds.limitMm);
    return `${left} ${t`That is below the legal limit of ${limitMm} mm, and it is illegal to drive on the tires.`}`;
  }
  const recommendedMm = formatMm(thresholds.recommendedMm);
  return `${left} ${t`That is below the recommended minimum of ${recommendedMm} mm.`}`;
};
